// One role a solution's declaration names (02-DESIGN/03-console/01-the-
// console.md § the address; § conventions: the moves, page by page — a
// solution's page; the moves of 2026-11-01; hq decision 0069):
// `solutions/<solution>/roles/<role>`, opened from the role's row on *What it
// may do*, or on the declaration a solution not installed shows with
// *Install*. The full lists — what it may do in the person's words with the
// platform's patterns under *In the platform's words*, *Across workspaces*,
// *Instructed by*, *Only an admin may*, *Answers as*, *Needs*, *May reach* —
// read from the install record where the solution is installed, and from
// the catalogue where it is not; installed, the workload the role runs as is
// a link to its page where the role is offered it. The crumb goes back to
// the solution.

import { Link, useParams } from "react-router";

import { tenantPath } from "../../address/address.ts";
import { isRefusal } from "../../platform/bus.ts";
import { pageOffered } from "../../roles/offers.ts";
import { Empty, NotAllowed, PageHead, Pending, useLoad } from "../../ui/components.tsx";
import {
  Code,
  SERVICE,
  Subjects,
  type Manifest,
  type Publication,
  type Record_,
  type RevisionRequest,
} from "../../wire/solutions.ts";
import { useSurface } from "../surface.tsx";
import { WorkloadLists } from "./Declaration.tsx";
import { MAY_TAB } from "./SolutionPage.tsx";
import { countedLine } from "./words.ts";

/** Read is what the role's page reads its declaration from: the install record, or the catalogue's entry. */
type Read =
  { kind: "installed"; r: Record_; m: Manifest } | { kind: "available"; p: Publication } | { kind: "none" };

export function RolePage() {
  const { name = "", role = "" } = useParams();
  const s = useSurface();
  const [load] = useLoad(`solution/role/${name}`, async (): Promise<Read> => {
    try {
      const r = await s.bus.call<Record_>(SERVICE, Subjects.installedGet(name), undefined);
      if (r.manifest !== undefined) {
        return { kind: "installed", r, m: r.manifest };
      }
    } catch (err) {
      if (!isRefusal(err, Code.notFound)) {
        throw err;
      }
    }
    try {
      const p = await s.bus.call<Publication>(
        SERVICE,
        Subjects.catalogueGet(name),
        {} satisfies RevisionRequest,
      );
      return { kind: "available", p };
    } catch (err) {
      if (isRefusal(err, Code.notFound)) {
        return { kind: "none" };
      }
      throw err;
    }
  });
  const display =
    load.state !== "ready"
      ? name
      : load.value.kind === "installed"
        ? load.value.m.display
        : load.value.kind === "available"
          ? load.value.p.manifest.display
          : name;
  const crumb = (
    <>
      <Link to={tenantPath(s.tenant, "solutions")}>Solutions</Link>
      <span>/</span>
      <Link to={tenantPath(s.tenant, "solutions", name)} state={{ tab: MAY_TAB }}>
        {display}
      </Link>
      <span>/</span>
      <span>{role}</span>
    </>
  );
  if (load.state === "pending") {
    return (
      <>
        <div className="crumb">{crumb}</div>
        <Pending what="Reading its declaration" />
      </>
    );
  }
  if (load.state === "refused") {
    return (
      <>
        <PageHead crumb={crumb} title={role} />
        <NotAllowed err={load.err} what="Its declaration" />
      </>
    );
  }
  const read = load.value;
  const m = read.kind === "installed" ? read.m : read.kind === "available" ? read.p.manifest : undefined;
  const w = m?.workloads?.find((x) => x.role === role);
  if (m === undefined || w === undefined) {
    return (
      <>
        <PageHead crumb={crumb} title={role} line="Not a workload this solution declares." />
        <Empty>{display} names no workload of that role.</Empty>
      </>
    );
  }
  const pinned = read.kind === "available" ? read.p.images?.[role] : undefined;
  const id = read.kind === "installed" ? read.r.workloads?.[role] : undefined;
  const placed =
    id === undefined
      ? undefined
      : {
          id,
          to: pageOffered(s.role, "workloads") ? tenantPath(s.tenant, "workloads", id) : undefined,
        };
  return (
    <>
      <PageHead crumb={crumb} title={w.role} line={countedLine(w)} />
      <section className="section">
        <WorkloadLists w={w} pinned={pinned} placed={placed} />
      </section>
    </>
  );
}
